import { cn, cv, NcButton, NcButtonProps, type VariantProps } from "@noted/noted-components";
import { ComponentProps, createContext, HTMLAttributes, useContext } from "react";
import { NavLink } from "react-router-dom";

import { NavigableListItem } from "~/components/navigable-list/navigable-list";

const subNavigationVariants = cv(
  "flex gap-2 flex-shrink-0 overflow-auto z-1 p-3 sm:flex-col sm:min-w-42",
  {
    variants: {
      variant: {
        directDescendant: "bg-main-dark",
        indirectDescendant: "",
      },
    },
    defaultVariants: {
      variant: "directDescendant",
    },
  }
);

const SubNavigationContext = createContext<VariantProps<typeof subNavigationVariants>>({
  variant: "directDescendant",
});

interface SubNavigationProps
  extends VariantProps<typeof subNavigationVariants>,
    HTMLAttributes<HTMLUListElement> {}

export const SubNavigation = ({ children, variant, className, ...props }: SubNavigationProps) => {
  return (
    <SubNavigationContext.Provider value={{ variant }}>
      <nav className="contents">
        <ul className={cn(subNavigationVariants({ variant }), className)} {...props}>
          {children}
        </ul>
      </nav>
    </SubNavigationContext.Provider>
  );
};

const linkVariants = cv(
  cn(
    "relative items-center flex rounded pl-5 pr-7 py-3 transition-colors duration-150 ease-in-out",
    "focus-visible:underline focus-visible:outline-current focus-visible:outline-2 -outline-offset-2"
  ),
  {
    variants: {
      variant: {
        directDescendant: "text-light",
        indirectDescendant: "text-active",
      },
      isActive: {
        true: "bg-main text-light cursor-default before:absolute before:top-0.5 before:bottom-0.5 before:left-0 before:w-1.5 before:rounded-r-sm",
        false: "cursor-pointer",
      },
    },
    compoundVariants: [
      {
        variant: "directDescendant",
        isActive: false,
        class: "hover:bg-active focus:bg-active",
      },
      {
        variant: "directDescendant",
        isActive: true,
        class: "before:bg-active",
      },
      {
        variant: "indirectDescendant",
        isActive: false,
        class: "hover:bg-focused focus:bg-focused",
      },
      {
        variant: "indirectDescendant",
        isActive: true,
        class: "before:bg-ui/75 focus-visible:outline-main",
      },
    ],
    defaultVariants: {
      variant: "directDescendant",
    },
  }
);

type LinkProps = ComponentProps<typeof NavLink>;

const Link = ({ children, className, ...props }: LinkProps) => {
  const { variant } = useContext(SubNavigationContext);
  return (
    <NavigableListItem role="none">
      <NavLink
        className={({ isActive }) => cn(linkVariants({ variant, isActive }), className)}
        {...props}
      >
        {children}
      </NavLink>
    </NavigableListItem>
  );
};

interface ButtonProps extends NcButtonProps {
  isActive?: boolean;
}

const Button = ({ children, className, isActive, ...props }: ButtonProps) => {
  const { variant } = useContext(SubNavigationContext);
  return (
    <NavigableListItem role="none">
      <NcButton
        variant="trigger"
        className={cn(
          linkVariants({ variant, isActive }),
          "w-full justify-start data-disabled:bg-transparent",
          className
        )}
        {...props}
      >
        {children}
      </NcButton>
    </NavigableListItem>
  );
};

SubNavigation.Link = Link;
SubNavigation.Button = Button;
